// src/components/Screening/LanguagesStep.jsx
import React from 'react'
import { Globe, CheckCircle } from 'lucide-react'
import GlowingButton from '../Common/GlowingButton'

const LanguagesStep = ({ criteria, onCriteriaChange, onNext, onPrev }) => {
  return (
    <div className="space-y-8">
      <div className="text-center">
        <div className="relative inline-block mb-6">
          <div className="absolute inset-0 bg-gradient-to-r from-teal-500 to-blue-500 rounded-3xl blur-lg opacity-50 animate-pulse"></div>
          <div className="relative w-20 h-20 bg-gradient-to-r from-teal-500 to-blue-500 rounded-3xl flex items-center justify-center shadow-2xl">
            <Globe className="w-10 h-10 text-white" />
          </div>
        </div>
        <h2 className="text-4xl font-bold text-white mb-4">Compétences Linguistiques</h2>
        <p className="text-gray-400 text-lg max-w-3xl mx-auto leading-relaxed">
          Des langues spécifiques sont-elles requises pour ce poste ?
        </p>
      </div>

      <div className="max-w-4xl mx-auto space-y-6">
        {/* Activation des langues */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-white mb-2">
                Des compétences linguistiques spécifiques sont-elles requises ?
              </h3>
              <p className="text-gray-400">
                Activez cette option si le poste nécessite des langues particulières
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={criteria.languagesImportant || false}
                onChange={(e) => onCriteriaChange('languagesImportant', e.target.checked)}
                className="sr-only"
              />
              <div className={`w-14 h-8 rounded-full transition-colors ${
                criteria.languagesImportant ? 'bg-teal-500' : 'bg-gray-600'
              }`}>
                <div className={`w-6 h-6 bg-white rounded-full mt-1 ml-1 transition-transform ${
                  criteria.languagesImportant ? 'translate-x-6' : ''
                }`}></div>
              </div>
            </label>
          </div>
        </div>

        {/* Configuration des langues */}
        {criteria.languagesImportant && (
          <div className="glass-card p-6">
            <h4 className="text-white font-medium mb-4 flex items-center">
              <span className="text-2xl mr-3">🗣️</span>
              Langues requises
            </h4>
            
            <div className="grid md:grid-cols-2 gap-4">
              {/* Langues principales */}
              {[
                { code: 'Français', flag: '🇫🇷', defaultLevel: 'Courant' },
                { code: 'Anglais', flag: '🇺🇸', defaultLevel: 'Intermédiaire' },
                { code: 'Espagnol', flag: '🇪🇸', defaultLevel: 'Intermédiaire' },
                { code: 'Allemand', flag: '🇩🇪', defaultLevel: 'Intermédiaire' },
                { code: 'Italien', flag: '🇮🇹', defaultLevel: 'Intermédiaire' },
                { code: 'Chinois', flag: '🇨🇳', defaultLevel: 'Intermédiaire' }
              ].map(({ code, flag, defaultLevel }) => {
                const isSelected = criteria.selectedLanguages?.some(l => l.langue === code)
                const currentLevel = criteria.selectedLanguages?.find(l => l.langue === code)?.niveau || defaultLevel

                return (
                  <div key={code} className="space-y-2">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isSelected || false}
                        onChange={(e) => {
                          let newLanguages = criteria.selectedLanguages || []
                          if (e.target.checked) {
                            newLanguages = [...newLanguages.filter(l => l.langue !== code), 
                                           { langue: code, niveau: defaultLevel }]
                          } else {
                            newLanguages = newLanguages.filter(l => l.langue !== code)
                          }
                          onCriteriaChange('selectedLanguages', newLanguages)
                        }}
                        className="w-4 h-4 text-teal-600 bg-gray-700 border-gray-600 rounded focus:ring-teal-500"
                      />
                      <span className="text-white">{flag} {code}</span>
                    </label>
                    {isSelected && (
                      <select
                        value={currentLevel}
                        onChange={(e) => {
                          let newLanguages = criteria.selectedLanguages || []
                          const langIndex = newLanguages.findIndex(l => l.langue === code)
                          if (langIndex >= 0) {
                            newLanguages[langIndex].niveau = e.target.value
                            onCriteriaChange('selectedLanguages', [...newLanguages])
                          }
                        }}
                        className="w-full p-2 text-sm border border-gray-600 rounded-lg bg-gray-800/50 text-white focus:ring-2 focus:ring-teal-500"
                      >
                        <option value="Débutant">Débutant</option>
                        <option value="Intermédiaire">Intermédiaire</option>
                        <option value="Avancé">Avancé</option>
                        <option value="Courant">Courant</option>
                        <option value="Natif">Natif</option>
                      </select>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Langue personnalisée */}
            <div className="mt-6 p-4 bg-white/5 rounded-lg border border-gray-600">
              <h5 className="text-white font-medium mb-3 flex items-center">
                <span className="text-xl mr-2">➕</span>
                Autre langue
              </h5>
              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder="Nom de la langue (ex: Japonais, Arabe...)"
                  className="flex-1 p-3 text-sm border border-gray-600 rounded-lg bg-gray-800/50 text-white focus:ring-2 focus:ring-teal-500 transition-all"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      let newLanguages = criteria.selectedLanguages || []
                      const customLang = { langue: e.target.value.trim(), niveau: 'Intermédiaire' }
                      newLanguages = [...newLanguages.filter(l => l.langue !== customLang.langue), customLang]
                      onCriteriaChange('selectedLanguages', newLanguages)
                      e.target.value = ''
                    }
                  }}
                />
                <div className="text-gray-400 text-sm flex items-center px-3">
                  Appuyez sur Entrée
                </div>
              </div>
            </div>
            
            {/* Résumé des langues sélectionnées */}
            {criteria.selectedLanguages && criteria.selectedLanguages.length > 0 && (
              <div className="mt-6 p-4 bg-teal-500/10 border border-teal-500/20 rounded-lg">
                <h6 className="text-teal-300 font-medium mb-2 flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  {criteria.selectedLanguages.length} langue(s) sélectionnée(s) :
                </h6>
                <div className="flex flex-wrap gap-2">
                  {criteria.selectedLanguages.map((lang, index) => (
                    <span key={index} className="px-3 py-1 bg-teal-500/20 border border-teal-500/30 rounded-full text-sm text-teal-200 flex items-center">
                      {lang.langue} ({lang.niveau})
                      <button
                        onClick={() => {
                          const newLanguages = criteria.selectedLanguages.filter((_, i) => i !== index)
                          onCriteriaChange('selectedLanguages', newLanguages)
                        }}
                        className="ml-2 text-teal-300 hover:text-red-400 transition-colors"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Message si pas de langues */}
        {!criteria.languagesImportant && (
          <div className="text-center p-8 bg-gray-500/10 border border-gray-500/20 rounded-lg">
            <h5 className="text-gray-300 font-medium mb-2">ℹ️ Langues non requises</h5>
            <p className="text-gray-400 text-sm">
              Le français sera considéré comme suffisant pour ce poste.
            </p>
          </div>
        )}
      </div>

      <div className="flex justify-center space-x-4 pt-8">
        <GlowingButton variant="secondary" onClick={onPrev}>
          ← Retour
        </GlowingButton>
        <GlowingButton onClick={onNext}>
          Continuer →
        </GlowingButton>
      </div>
    </div>
  )
}

export default LanguagesStep